import React, { useState, useEffect } from 'react';
import { Alert, Button, Badge, Spinner, Collapse, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faSync,
  faChevronDown,
  faChevronUp,
  faCheckCircle,
  faClock,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import {
  useFetchIngestionStatus,
  useTriggerBackfill,
  useFetchJobStatus,
  useFetchActiveFileIndexingJob,
} from '../../../services/casegpt';

const IngestionStatusAlert = ({ claimId }) => {
  const queryClient = useQueryClient();
  const [showDetails, setShowDetails] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);

  // Fetch ingestion status
  const {
    data: status,
    isLoading: statusLoading,
    isError: statusError,
    error: statusErrorData,
  } = useFetchIngestionStatus(claimId);

  // Check for existing active job on mount
  const { data: activeJobData } = useFetchActiveFileIndexingJob(claimId, {
    onSuccess: (data) => {
      if (data?.job?.jobId) {
        setActiveJobId(data.job.jobId);
      }
    },
  });

  // Fetch job status when we have an active job
  const {
    data: jobStatus,
    isLoading: jobLoading,
  } = useFetchJobStatus(activeJobId, {
    onSuccess: (data) => {
      // When job completes, refresh ingestion status and clear job ID
      if (data?.jobStatus === 'completed') {
        toast.success(
          `Indexing complete: ${data.result?.filesProcessed || 0} files processed`,
        );
        queryClient.invalidateQueries(['ingestion-status', claimId]);
        // Clear after a short delay to show completion state
        setTimeout(() => setActiveJobId(null), 2000);
      } else if (data?.jobStatus === 'failed') {
        toast.error(`Indexing failed: ${data.errorMessage || 'Unknown error'}`);
        setTimeout(() => setActiveJobId(null), 2000);
      }
    },
  });

  // Set active job from check on mount
  useEffect(() => {
    if (activeJobData?.job?.jobId && !activeJobId) {
      setActiveJobId(activeJobData.job.jobId);
    }
  }, [activeJobData, activeJobId]);

  // Trigger backfill mutation
  const triggerBackfill = useTriggerBackfill(
    (result) => {
      if (result.jobId) {
        setActiveJobId(result.jobId);
        if (result.isExisting) {
          toast.info('Indexing job already in progress');
        } else {
          toast.info(`Started indexing ${result.filesToProcess || 'files'}...`);
        }
      } else if (result.filesProcessed === 0) {
        toast.info('No files need indexing');
      }
    },
    {
      onError: (err) =>
        toast.error(err.response?.data?.msg || 'Failed to start indexing'),
    },
  );

  // Check if job is actively processing
  const isJobActive =
    jobStatus?.jobStatus === 'pending' || jobStatus?.jobStatus === 'processing';
  const isJobComplete = jobStatus?.jobStatus === 'completed';
  const isJobFailed = jobStatus?.jobStatus === 'failed';

  // Calculate progress percentage
  const progressPercent =
    jobStatus && jobStatus.totalItems > 0
      ? Math.round((jobStatus.processedItems / jobStatus.totalItems) * 100)
      : 0;

  // Don't show anything while loading initial status
  if (statusLoading && !activeJobId) {
    return (
      <Alert variant="info" className="mb-3">
        <Spinner size="sm" className="me-2" />
        Checking AI search index status...
      </Alert>
    );
  }

  // Show error state
  if (statusError && !activeJobId) {
    return (
      <Alert variant="secondary" className="mb-3">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        Unable to check AI search index status
        {statusErrorData?.response?.status === 403 && ' (admin only)'}
      </Alert>
    );
  }

  // Show job progress if a job is active
  if (activeJobId && (isJobActive || isJobComplete || isJobFailed)) {
    return (
      <Alert
        variant={isJobFailed ? 'danger' : isJobComplete ? 'success' : 'info'}
        className="mb-3"
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            {isJobActive && (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                <strong>Indexing in Progress</strong>
                {jobStatus.totalItems > 0 && (
                  <span className="ms-2">
                    {jobStatus.processedItems} / {jobStatus.totalItems} files
                  </span>
                )}
              </>
            )}
            {isJobComplete && (
              <>
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                <strong>Indexing Complete</strong>
                <span className="ms-2">
                  {jobStatus.result?.filesProcessed || 0} files indexed
                  {jobStatus.result?.filesFailed > 0 &&
                    `, ${jobStatus.result.filesFailed} failed`}
                </span>
              </>
            )}
            {isJobFailed && (
              <>
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>Indexing Failed</strong>
                <span className="ms-2 text-muted">
                  {jobStatus.errorMessage || 'Unknown error'}
                </span>
              </>
            )}
          </div>
          {isJobActive && jobStatus?.jobStatus === 'pending' && (
            <Badge bg="secondary">
              <FontAwesomeIcon icon={faClock} className="me-1" />
              Queued
            </Badge>
          )}
        </div>
        {isJobActive && jobStatus.totalItems > 0 && (
          <ProgressBar
            now={progressPercent}
            label={`${progressPercent}%`}
            className="mt-2"
            animated
            striped
          />
        )}
      </Alert>
    );
  }

  // All files are indexed - show nothing
  if (!status || status.allIngested) {
    return null;
  }

  const notIngestedFiles = status.files.filter((f) => !f.isIngested);

  return (
    <Alert variant="warning" className="mb-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>AI Search Incomplete</strong>
          <span className="ms-2">
            {status.notIngested} of {status.totalFiles} files not indexed
          </span>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            aria-expanded={showDetails}
          >
            <FontAwesomeIcon
              icon={showDetails ? faChevronUp : faChevronDown}
              className="me-1"
            />
            {showDetails ? 'Hide' : 'Details'}
          </Button>
          <Button
            variant="warning"
            size="sm"
            onClick={() => triggerBackfill.mutate({ claimId })}
            disabled={triggerBackfill.isLoading}
          >
            {triggerBackfill.isLoading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Starting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSync} className="me-1" />
                Index Files
              </>
            )}
          </Button>
        </div>
      </div>

      <Collapse in={showDetails}>
        <div className="mt-3">
          <div className="mb-2">
            <Badge bg="success" className="me-2">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              {status.ingested} Indexed
            </Badge>
            <Badge bg="warning" text="dark">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
              {status.notIngested} Not Indexed
            </Badge>
          </div>
          <div
            className="small border rounded p-2 bg-light"
            style={{ maxHeight: 200, overflowY: 'auto' }}
          >
            <div className="fw-bold mb-2 text-muted">Files needing indexing:</div>
            {notIngestedFiles.map((file) => (
              <div
                key={file.fileId}
                className="d-flex justify-content-between align-items-center py-1 border-bottom"
              >
                <span
                  className="text-truncate"
                  style={{ maxWidth: '80%' }}
                  title={file.fileName}
                >
                  {file.fileName}
                </span>
                <Badge bg="warning" text="dark" className="ms-2">
                  pending
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </Collapse>
    </Alert>
  );
};

export default IngestionStatusAlert;
